import { Easing, Tween } from "@tweenjs/tween.js";
import Hammer from "hammerjs";

import type { IProfile } from "@/data/model/Profile";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { DomUtil } from "@/shared/ui/Helper";

import { OperatorClient } from "@/operator/OperatorClient";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";

export class UserMenu {
    private $element: JQuery;

    private $nav?: JQuery;
    private $dim?: JQuery;

    private runningTween: any;
    private touchCtrl?: HammerManager;
    private isReleasing = false;
    private currentNavPosition?: number;
    private menuWidth = 270;

    constructor() {
        this.$element = $('<div class="usermenu" />');
        const burger = $('<div class="opener"><i class="fal fa-bars"></i></div>').appendTo(this.$element);

        burger.click((e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.$nav === undefined) this.openNav();
        });
    }

    public showRoomPlan() {
        this.closeNav();
        operatorEvents.views.ShowRoomPlan.dispatch({ showSettingsRoomPlan: true });
    }

    public showProduction() {
        this.closeNav();
        operatorEvents.views.ShowProduction.dispatch();
    }

    public showItems() {
        this.closeNav();
        operatorEvents.views.ShowItems.dispatch();
    }

    public showOpenBills() {
        this.closeNav();
        operatorEvents.views.ShowOpenBills.dispatch();
    }

    public showOrders() {
        this.closeNav();
        operatorEvents.views.ShowOrders.dispatch();
    }

    public showSettings() {
        this.closeNav();
        operatorEvents.views.ShowSettings.dispatch();
    }

    public showReports() {
        this.closeNav();
        operatorEvents.views.ShowReports.dispatch();
    }

    public showPayments() {
        this.closeNav();
        operatorEvents.views.ShowPayments.dispatch();
    }

    private openNav() {
        if (this.$nav === undefined) {
            this.$nav = $(
                '<div class="nav dragable"><div class="head"></div><div class="inner scrollable"></div></div>'
            ).appendTo(this.$element);
            this.buildNavContent(this.$nav);

            this.touchCtrl = new Hammer(this.$nav[0], {
                recognizers: [[Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 }]],

                domEvents: false,
            });

            this.touchCtrl.on("panstart", () => {
                if (this.runningTween !== undefined) {
                    this.runningTween.stop();
                    this.runningTween = undefined;
                }

                this.isReleasing = false;
                this.currentNavPosition = DomUtil.translatePosition(this.$nav!).x;
            });

            this.touchCtrl.on("panmove", (ev) => {
                this.moveNav(ev.deltaX);
            });

            this.touchCtrl.on("panend", () => {
                if (this.isReleasing) return;

                this.isReleasing = true;
                this.releaseNav();
            });
        }

        if (this.$dim === undefined) {
            const parentView = this.$element.parents(".view");
            this.$dim = $('<div class="navdim" />')
                .prependTo(parentView)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeNav();
                });
        }

        this.tweenTo(false, true);
    }

    private buildNavContent($nav: JQuery) {
        const head = $nav.find("> .head");
        const inner = $nav.find("> .inner");

        $nav.find("> .signout").remove();

        head.empty();

        const avatar = $('<div class="avatar" />')
            .appendTo(head)
            .click((e) => {
                e.preventDefault();
            });

        if (OperatorClient.context.profile!.avatar === undefined)
            avatar
                .addClass("green")
                .append(
                    '<div class="shortname">' +
                        Format.htmlEscape(UserMenu.shortNameFromProfile(OperatorClient.context.profile!)) +
                        "</div>"
                );
        else
            avatar.append(
                '<div class="img" style="background-image:url(\'' + OperatorClient.context.profile!.avatar + "');\" />"
            );

        $(
            '<div class="name green">' +
                Format.htmlEscape(
                    OperatorClient.context.profile!.firstName + " " + OperatorClient.context.profile!.lastName
                ) +
                "</div>"
        )
            .appendTo(head)
            .click((e) => {
                e.preventDefault();
            });

        inner.empty();

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-hourglass-start"></i></div>' +
                trans.navigation.production +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showProduction();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-shopping-cart"></i></div>' +
                trans.navigation.orders +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showOrders();
            });

        $('<a class="entry"><div class="icon"><i class="fal fa-list"></i></div>' + trans.navigation.items + "</a>")
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showItems();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-receipt"></i></div>' +
                trans.navigation.openBills +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showOpenBills();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-border-style-alt"></i></div>' +
                trans.navigation.rooms +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();

                this.showRoomPlan();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-money-bill-alt"></i></div>' +
                trans.navigation.payments +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showPayments();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-file-spreadsheet"></i></div>' +
                trans.navigation.reports +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showReports();
            });

        $(
            '<a class="entry"><div class="icon"><i class="fal fa-user-cog"></i></div>' +
                trans.navigation.settings +
                "</a>"
        )
            .appendTo(inner)
            .click((e) => {
                e.preventDefault();
                this.showSettings();
            });

        const signoutEntry = $(
            '<a class="signout entry"><div class="icon"><i class="fal fa-sign-out"></i></div>' +
                trans.navigation.logout +
                "</a>"
        )
            .appendTo($nav)
            .click((e) => {
                e.preventDefault();

                this.setEntryLoading(signoutEntry, true);
                OperatorClient.context
                    .signOut()
                    .then(() => {
                        this.setEntryLoading(signoutEntry, false);
                        this.closeNav();
                    })
                    .catch((ex) => {
                        logError(ex);
                        this.setEntryLoading(signoutEntry, false);
                    });
            });
    }

    public closeNav() {
        if (this.$nav === undefined) return;

        this.tweenTo(true, false);
    }

    private onNavClosed() {
        this.$dim!.remove();
        this.$nav!.remove();
        this.$nav = undefined;
        this.$dim = undefined;
        this.touchCtrl!.destroy();
        this.touchCtrl = undefined;
    }

    private moveNav(byPixel: number) {
        let newPosition = this.currentNavPosition! + byPixel;
        let overdraging = 0;
        if (newPosition < 0) overdraging = newPosition;

        let p = 1 - (1 / this.menuWidth) * newPosition;
        if (p > 1) p = 1;
        else if (p < 0) p = 0;

        newPosition += DomUtil.momentumResistance(overdraging, 40);
        this.$dim!.css("opacity", p);
        this.$nav!.css({ transform: "translate3d(" + newPosition + "px, 0, 0)" });
    }

    private releaseNav() {
        const currentPos = DomUtil.translatePosition(this.$nav!).x;
        this.tweenTo(currentPos > 50, false);
    }

    private tweenTo(hidden: boolean, useBounce: boolean) {
        const current = {
            pos: DomUtil.translatePosition(this.$nav!).x,
            opacity: this.$dim!.css("opacity"),
        };

        const dest = {
            pos: hidden ? this.menuWidth : 0,
            opacity: hidden ? 0 : 1,
        };

        this.runningTween = new Tween(current)
            .to(dest, 300)
            .easing(useBounce ? Easing.Back.Out : Easing.Exponential.Out)
            .onUpdate((f) => {
                this.$dim!.css("opacity", f.opacity);
                this.$nav!.css({ transform: "translate3d(" + f.pos + "px, 0, 0)" });
            })
            .start();

        this.runningTween.onComplete(() => {
            if (hidden) this.onNavClosed();

            this.isReleasing = false;
            this.runningTween = undefined;
        });

        const animate = (time?: null) => {
            if (this.runningTween !== undefined && this.runningTween.isPlaying()) {
                this.runningTween.update(time);
                requestAnimationFrame(animate as any);
            }
        };

        animate();
    }

    private setEntryLoading($entry: JQuery, isLoading: boolean) {
        const parent = $entry.find("> .icon");
        parent.find("> .loader").remove();

        if (isLoading) {
            parent.append('<div class="loader" />');
            parent.find("> .fal").hide();
        } else parent.find("> .fal").show();
    }

    private static shortNameFromProfile(profile: IProfile): string {
        let sn = "";

        if (profile.firstName !== undefined && profile.firstName.length > 0) sn += profile.firstName.substr(0, 1);

        if (profile.lastName !== undefined && profile.lastName.length > 0) sn += profile.lastName.substr(0, 1);

        return sn;
    }

    public appendTo($element: JQuery): UserMenu {
        this.$element.appendTo($element);
        return this;
    }
}
